import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  #pushCartMutationEvent(line, quantity, action) {
    const row = this.refs.cartItemRows[line - 1];
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    const previousQuantity = Number(quantityInput?.defaultValue || quantityInput?.getAttribute('value') || 0);
    const variantId = quantitySelector?.dataset.variantId || '';
    const productLink = row?.querySelector('.cart-items__title');
    const productName = productLink?.textContent?.trim() || '';
    const sku = row?.getAttribute('data-product-sku') || quantitySelector?.dataset.productSku || '';

    const globalWindow = /** @type {any} */ (window);

    if (quantity > 0 && action === 'change') {
      globalWindow.pushGtmEvent?.({
        event: 'cart_quantity_update',
        product_id: variantId,
        product_name: productName,
        sku,
        quantity,
        previous_quantity: previousQuantity,
        url: productLink?.getAttribute('href') || window.location.pathname,
      });
    }

    if (quantity === 0 || quantity < previousQuantity) {
      globalWindow.pushGtmEvent?.({
        event: 'remove_from_cart',
        product_id: variantId,
        product_name: productName,
        sku,
        quantity_removed: quantity === 0 ? previousQuantity : previousQuantity - quantity,
        quantity_remaining: quantity,
        action: quantity === 0 ? 'remove' : 'decrease_quantity',
        url: productLink?.getAttribute('href') || window.location.pathname,
      });
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    // Cart lines cannot be cleared via the quantity selector; use remove instead.
    if (!quantity || quantity < 1) return;

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;
    const optimisticItemCount = Math.max(this.#getCurrentCartItemCount() - this.#getLineItemQuantity(line), 0);

    const template = document.getElementById('empty-cart-template');

    if (isEmptyCart && template instanceof HTMLTemplateElement && this.isDrawer) {
      this.#setCartDrawerEmptyState(0);
      this.#dispatchCartUpdate({ item_count: 0, items: [] }, 0);

      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, ['empty-cart-drawer']);

      this.#initializeEmptyCartRecommendations();

      return;
    }

    this.#renderHeaderCartBubble(optimisticItemCount);

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        this.#pushCartMutationEvent(line, quantity, config.action);

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
        this.#setCartDrawerEmptyState(newCartItemCount);

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.#dispatchCartUpdate(parsedResponseText, newCartItemCount, parsedResponseText.sections);

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full', {
          injectStylesheet: true,
          cartLineKeys: true,
        });

        if (typeof window.scheduleCartSavingsUpdate === 'function') {
          window.scheduleCartSavingsUpdate(parsedResponseText);
        } else if (typeof window.updateCartSavings === 'function') {
          window.updateCartSavings(parsedResponseText);
        }

        if (this.isDrawer) {
          try {
            if (window.COUPON_SETTINGS?.enabled && typeof window.syncCouponBannerFromDOM === 'function') {
              window.syncCouponBannerFromDOM();
            }
          } catch (e) {}
        }

        this.#applyServerLineQuantity(parsedResponseText, line, quantity);
        this.#updateCartQuantitySelectorButtonStates();
        if (newCartItemCount === 0) this.#initializeEmptyCartRecommendations();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      const startedAt = performance.now();
      const globalWindow = /** @type {any} */ (window);
      if (globalWindow.CART_DISCOUNT_DEBUG || localStorage.getItem('cartDiscountDebug') === '1') {
        console.log('[CartDiscount][handleCartUpdate] DiscountUpdateEvent — re-rendering section', {
          sectionId: this.sectionId,
          sourceId: event.detail?.sourceId,
        });
      }
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
        if (typeof window.scheduleCartSavingsUpdate === 'function') {
          window.scheduleCartSavingsUpdate();
        } else if (typeof window.updateCartSavings === 'function') {
          window.updateCartSavings();
        }
        if (globalWindow.CART_DISCOUNT_DEBUG || localStorage.getItem('cartDiscountDebug') === '1') {
          console.log(
            `[CartDiscount][handleCartUpdate] section re-render completed in ${(performance.now() - startedAt).toFixed(0)}ms`,
            { sectionId: this.sectionId }
          );
        }
      });
      return;
    }
    if (event.target === this) return;

    const cartUpdateSource = event.detail?.sourceId || event.detail?.data?.source || '';
    if (cartUpdateSource === 'cart-reward-selector') return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    const didError = Boolean(event.detail?.data?.didError);

    // Inventory / max-qty ATC failures often ship no section HTML. A blind re-fetch
    // (especially on PLP) triggers 429s and morph errors for unrelated sections.
    if (didError && !cartItemsHtml) {
      return;
    }

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, 'full', { injectStylesheet: true, cartLineKeys: true });

      if (typeof window.scheduleCartSavingsUpdate === 'function') {
        window.scheduleCartSavingsUpdate(event.detail.resource);
      } else if (typeof window.updateCartSavings === 'function') {
        window.updateCartSavings(event.detail.resource);
      }

      if (this.isDrawer) {
        try {
          if (window.COUPON_SETTINGS?.enabled && typeof window.syncCouponBannerFromDOM === 'function') {
            window.syncCouponBannerFromDOM();
          }
        } catch (e) {}
      }

      const dialog = document.querySelector('.cart-drawer__dialog');
      if (dialog) this.#setCartDrawerEmptyState(Number(event.detail?.data?.itemCount ?? 0));

      // Drawer morph does not rewrite the quantity input, so keep it on the cart quantity.
      this.#applyServerLineQuantity(event.detail?.resource);
      this.#updateCartQuantitySelectorButtonStates();
      if (Number(event.detail?.data?.itemCount ?? 0) === 0) this.#initializeEmptyCartRecommendations();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
        if (typeof window.scheduleCartSavingsUpdate === 'function') {
          window.scheduleCartSavingsUpdate(event.detail.resource);
        } else if (typeof window.updateCartSavings === 'function') {
          window.updateCartSavings(event.detail.resource);
        }
      });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Writes the cart line quantity back onto the drawer selector.
   * A click can raise the number before the cart rejects the extra units.
   * @param {Object} cart
   * @param {number} [requestedLine]
   * @param {number} [requestedQty]
   */
  #applyServerLineQuantity(cart, requestedLine, requestedQty) {
    if (!cart?.items) return;
    const selectors = this.querySelectorAll('cart-quantity-selector-component');
    for (const selector of selectors) {
      if (!(selector instanceof HTMLElement)) continue;
      const input = selector.querySelector('input[data-cart-line]');
      if (!(input instanceof HTMLInputElement)) continue;
      const line = Number(input.dataset.cartLine);
      const variantId = selector.dataset.variantId;
      let item = cart.items[line - 1];
      if (!item || (variantId && String(item.variant_id) !== variantId)) {
        item = cart.items.find((entry) => String(entry.variant_id) === variantId);
      }
      if (!item) continue;
      const qty = String(item.quantity);
      input.value = qty;
      input.defaultValue = qty;
      input.setAttribute('value', qty);
      input.setAttribute('data-cart-quantity', qty);
      if (requestedLine === line && Number.isFinite(requestedQty) && item.quantity < requestedQty) {
        input.max = qty;
        input.setAttribute('max', qty);
      }
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the current total cart item count rendered in this section.
   * @returns {number}
   */
  #getCurrentCartItemCount() {
    const cartItemCount = this.querySelector('[ref="cartItemCount"]')?.textContent;
    const count = Number.parseInt(cartItemCount ?? '0', 10);

    return Number.isNaN(count) ? 0 : count;
  }

  /**
   * Gets the quantity for a cart line before it is removed.
   * @param {number} line
   * @returns {number}
   */
  #getLineItemQuantity(line) {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    const quantity = Number(
      quantityInput?.value || quantityInput?.defaultValue || quantityInput?.getAttribute('value') || 0
    );

    return Number.isFinite(quantity) ? quantity : 0;
  }

  /**
   * Updates the header cart bubble immediately while the cart change request finishes.
   * @param {number} itemCount
   */
  #renderHeaderCartBubble(itemCount) {
    for (const cartIcon of document.querySelectorAll('cart-icon')) {
      const cartIconComponent =
        /** @type {{ renderCartBubble?: (itemCount: number, comingFromProductForm: boolean, animate?: boolean) => void }} */ (
          cartIcon
        );

      cartIconComponent.renderCartBubble?.(itemCount, false);
    }
  }

  /**
   * Toggles empty-state class on cart drawer dialog.
   * @param {number} itemCount
   */
  #setCartDrawerEmptyState(itemCount) {
    const dialog = document.querySelector('.cart-drawer__dialog');
    if (!dialog) return;

    dialog.classList.toggle('cart-drawer--empty', itemCount === 0);
  }

  /**
   * Dispatches cart updates so shared cart UI like the header bubble stays in sync.
   * @param {Object} cart
   * @param {number} itemCount
   * @param {Record<string, string>} [sections]
   */
  #dispatchCartUpdate(cart, itemCount, sections) {
    this.dispatchEvent(
      new CartUpdateEvent(cart, this.sectionId, {
        itemCount,
        source: 'cart-items-component',
        sections,
      })
    );
  }

  /**
   * Initializes empty cart recommendation slider interactions after dynamic DOM updates.
   * Inline scripts inside cloned/morphed markup do not execute automatically.
   */
  #initializeEmptyCartRecommendations() {
    const slider = document.querySelector('.cart-drawer-empty-recommendations__slider');
    if (!(slider instanceof HTMLElement) || slider.dataset.mouseScrollBound === 'true') return;

    slider.dataset.mouseScrollBound = 'true';

    slider.addEventListener(
      'wheel',
      (event) => {
        if (slider.scrollWidth <= slider.clientWidth) return;

        if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
          event.preventDefault();
          slider.scrollLeft += event.deltaY;
        }
      },
      { passive: false }
    );

    let isDown = false;
    let startX = 0;
    let scrollLeftStart = 0;

    slider.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;
      isDown = true;
      slider.classList.add('is-dragging');
      startX = event.pageX - slider.offsetLeft;
      scrollLeftStart = slider.scrollLeft;
    });

    slider.addEventListener('mouseleave', () => {
      isDown = false;
      slider.classList.remove('is-dragging');
    });

    slider.addEventListener('mouseup', () => {
      isDown = false;
      slider.classList.remove('is-dragging');
    });

    slider.addEventListener('mousemove', (event) => {
      if (!isDown) return;
      event.preventDefault();
      const x = event.pageX - slider.offsetLeft;
      const walk = (x - startX) * 1.2;
      slider.scrollLeft = scrollLeftStart - walk;
    });
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
